import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangleIcon, CheckCircle2Icon, Loader2Icon, SendIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useSlackSettings,
  useTestSlack,
  useUpdateSlackSettings,
} from '@/features/settings/use-settings';
import { handleApiError } from '@/lib/handle-error';

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  return (
    <AdminGuard>
      <SettingsContent />
    </AdminGuard>
  );
}

function SettingsContent() {
  const settings = useSlackSettings();
  const update = useUpdateSlackSettings();
  const test = useTestSlack();

  const [channel, setChannel] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!settings.data || dirty) return;
    setChannel(settings.data.channel);
    setEnabled(settings.data.enabled);
  }, [settings.data, dirty]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(
      { channel: channel.trim() || undefined, enabled },
      {
        onSuccess: () => {
          toast.success('Slack settings saved');
          setDirty(false);
        },
        onError: (err) => handleApiError(err, 'Failed to save settings'),
      },
    );
  };

  const handleTest = () => {
    test.mutate(undefined, {
      onSuccess: (data) => {
        if (data.ok) {
          toast.success('Test message sent to Slack');
        } else {
          toast.warning('Slack returned not-ok — check the bot token + channel.');
        }
      },
      onError: (err) => handleApiError(err, 'Failed to send test message'),
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Workspace-wide configuration for integrations and notifications.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Slack</CardTitle>
              <CardDescription>
                Default channel for daily / weekly summaries and webhook resource alerts.
              </CardDescription>
            </div>
            {settings.data && <BotTokenBadge configured={settings.data.botTokenConfigured} />}
          </div>
        </CardHeader>
        <CardContent>
          {settings.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : settings.isError ? (
            <p className="text-sm text-destructive">{settings.error.message}</p>
          ) : (
            <form className="grid gap-4" onSubmit={handleSave}>
              <div className="grid gap-2">
                <Label htmlFor="slack-channel">Default channel</Label>
                <Input
                  id="slack-channel"
                  value={channel}
                  onChange={(e) => {
                    setChannel(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="#insight-metabase"
                />
                <p className="text-xs text-muted-foreground">
                  Slack channel name (`#channel-name`) or user id. Used when a sender doesn't pin a
                  specific channel (e.g. cron summaries).
                </p>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="slack-enabled"
                  checked={enabled}
                  onCheckedChange={(c) => {
                    setEnabled(c === true);
                    setDirty(true);
                  }}
                />
                <Label htmlFor="slack-enabled" className="leading-snug font-normal">
                  <span className="font-medium">Enable Slack notifications</span>
                  <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                    When off, the API skips Slack calls that don't pin a specific channel.
                    Per-recipient DMs (e.g. announcement broadcasts) still go out because they pin
                    the channel explicitly.
                  </span>
                </Label>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={handleTest}
                  disabled={test.isPending || !settings.data?.botTokenConfigured}
                >
                  {test.isPending ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <SendIcon className="size-4" />
                  )}
                  Send test message
                </Button>
                <Button type="submit" disabled={update.isPending || !dirty}>
                  {update.isPending && <Loader2Icon className="size-4 animate-spin" />}
                  Save changes
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BotTokenBadge({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <Badge variant="outline" className="gap-1">
        <CheckCircle2Icon className="size-3 text-emerald-500" />
        Bot token set
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 border-destructive/40 text-destructive">
      <AlertTriangleIcon className="size-3" />
      Bot token missing
    </Badge>
  );
}
